<template>
  <main class="l-main p-register set">
    <div class="l-block">
      <SetLocalNav :hasProject="hasProject" />
      <TitleRegister>
        <Progress :items="progress" />
      </TitleRegister>
      <div class="l-block__content">
        <div class="hTitle">
          <h1>Google Analytics4を連携します。</h1>
        </div>
        <Loading v-if="isLoading.ga4Accounts" transparent />
        <div v-else-if="errorGa4Accounts">
          <Alert class="l-mt-3">{{ errorGa4Accounts }}</Alert>
          <div class="btnArea">
            <div class="form-auto">
              <Button
                :to="{ name: 'set' }"
                variant="border"
                size="w260"
                fsize="f16"
                icon="return_gn"
              >
                戻る
              </Button>
            </div>
          </div>
        </div>
        <template v-else>
          <div class="flex">
            <div class="title">
              <p>アカウント</p>
            </div>
            <div class="form c-alertPos">
              <Select
                :options="ga4Accounts"
                :selected="params.ga4AccountId"
                :disabled="!ga4Accounts.length"
                required
                placeholder="アカウントを選択してください"
                :class="{
                  alert:
                    errorGa4Properties ||
                    (isEdited.ga4AccountId && error.ga4AccountId)
                }"
                @change="changeAccount"
              />
              <Alert v-if="errorGa4Properties" class="l-mt-2">
                {{ errorGa4Properties }}
              </Alert>
              <Alert
                v-else-if="isEdited.ga4AccountId && error.ga4AccountId"
                class="l-mt-2"
              >
                {{ error.ga4AccountId }}
              </Alert>
            </div>
          </div>
          <div class="flex">
            <div class="title">
              <p>プロパティ</p>
            </div>
            <div class="form c-alertPos">
              <Select
                :options="ga4Properties"
                :selected="params.ga4PropertyId"
                :disabled="!ga4Properties.length"
                required
                placeholder="アカウントを選択後、プロパティを選択してください"
                :class="{
                  alert:
                    errorGa4DataStreams ||
                    (isEdited.ga4PropertyId && error.ga4PropertyId)
                }"
                @change="changeProperty"
              />
              <Alert v-if="errorGa4DataStreams" class="l-mt-2">
                {{ errorGa4DataStreams }}
              </Alert>
              <Alert
                v-else-if="isEdited.ga4PropertyId && error.ga4PropertyId"
                class="l-mt-2"
              >
                {{ error.ga4PropertyId }}
              </Alert>
              <!-- <p class="ga-connect-announcement">
                ※UAのプロパティは選択できません。GA4の導入については<a
                  href="https://searchwrite.jp/news/information/1866/"
                  target="_blank"
                  rel="noreferrer"
                  >こちら</a
                >
              </p> -->
            </div>
          </div>
          <div class="flex">
            <div class="title">
              <p>データストリーム</p>
            </div>
            <div class="form c-alertPos">
              <Select
                :options="ga4DataStreams"
                :selected="params.ga4DataStreamId"
                :disabled="!ga4DataStreams.length"
                required
                placeholder="プロパティを選択後、データストリームを選択してください"
                :class="{
                  alert: isEdited.ga4DataStreamId && error.ga4DataStreamId
                }"
                @change="changeDataStream"
              />
              <Alert
                v-if="isEdited.ga4DataStreamId && error.ga4DataStreamId"
                class="l-mt-2"
              >
                {{ error.ga4DataStreamId }}
              </Alert>
            </div>
          </div>
          <div class="btnArea">
            <div class="form-auto">
              <Button
                variant="main"
                size="w380"
                fsize="f16"
                icon="arrow_r"
                :disabled="isError"
                @click="submit"
              >
                Google Search Consoleの連携に進む
              </Button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </main>
</template>

<script>
import { ERROR_MESSAGE } from '@/constants';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Loading from '@/components/Loading';
import Progress from '@/components/Progress';
import Select from '@/components/Select';
import SetLocalNav from '@/components/set/SetLocalNav';
import TitleRegister from '@/components/TitleRegister';
import googleGA4API from '@/auth/GoogleGA4API';
import utils from '@/utils';
import Validation from '@/validation';

export default {
  props: ['hasProject'],

  components: {
    Alert,
    Button,
    Loading,
    Progress,
    Select,
    SetLocalNav,
    TitleRegister
  },

  data() {
    return {
      data: Object.assign({}, this.$store.state.set.data),
      error: {
        ga4AccountId: '',
        ga4PropertyId: '',
        ga4DataStreamId: ''
      },
      googleGA4API,
      params: Object.assign({}, this.$store.state.set.params),
      isEdited: Object.assign({}, this.$store.state.set.isEdited),
      isError: false,
      isLoading: {
        ga4Accounts: true,
        ga4Properties: true,
        ga4DataStreams: true
      },
      progress: [
        {
          current: true,
          label: 'Google Analyticsの登録'
        },
        {
          label: 'Google Search Consoleの登録'
        },
        {
          label: '確認'
        }
      ]
    };
  },

  computed: {
    errorGa4Accounts() {
      if (!this.isLoading.ga4Accounts && !this.data.ga4Accounts.length) {
        return ERROR_MESSAGE.ga4.no_account;
      }
      return '';
    },
    errorGa4Properties() {
      if (!this.isLoading.ga4Properties && !this.data.ga4Properties.length) {
        return ERROR_MESSAGE.ga4.no_property;
      }
      return '';
    },
    errorGa4DataStreams() {
      if (!this.isLoading.ga4DataStreams && !this.data.ga4DataStreams.length) {
        return ERROR_MESSAGE.ga4.no_data_stream;
      }
      return '';
    },
    ga4Accounts() {
      return this.getSelectOptions(this.data.ga4Accounts);
    },
    ga4Properties() {
      return this.getSelectOptions(this.data.ga4Properties);
    },
    ga4DataStreams() {
      return this.getSelectOptions(this.data.ga4DataStreams);
    }
  },

  watch: {
    error: {
      handler() {
        this.checkError();
      },
      deep: true
    },
    'params.ga4AccountId': {
      handler(value, oldValue) {
        this.error.ga4AccountId = new Validation()
          .select()
          .test(this.params.ga4AccountId);
      },
      immediate: true
    },
    'params.ga4PropertyId': {
      handler(value, oldValue) {
        this.error.ga4PropertyId = new Validation()
          .select()
          .test(this.params.ga4PropertyId);
      },
      immediate: true
    },
    'params.ga4DataStreamId': {
      handler(value, oldValue) {
        this.error.ga4DataStreamId = new Validation()
          .select()
          .test(this.params.ga4DataStreamId);
      },
      immediate: true
    }
  },

  created() {
    this.auth
      .getUserData()
      .then(response => {
        const projects = response.data.user_metadata.projects;
        let gaAccessToken = '';
        for (let i = 0; i < projects.length; i++) {
          if (projects[i].tmpKey === this.params.tmpKey) {
            gaAccessToken = projects[i].gaAccessToken;
            break;
          }
        }
        if (!gaAccessToken) {
          console.error('ga-0001');
          this.goErrorPage();
          return;
        }
        this.googleGA4API.setToken(gaAccessToken);
        this.getGA4Accounts();
      })
      .catch(error => {
        console.error('ga-0003', error);
        this.goErrorPage();
      });
  },

  methods: {
    checkError() {
      this.isError = utils.checkError(this.error);
    },

    changeAccount(value) {
      this.isEdited.ga4AccountId = true;
      this.params.ga4AccountId = value;
      this.resetGA4Properties();
      this.resetGA4DataStreams();
      this.getGA4Properties();
      this.updateParams();
    },

    changeProperty(value) {
      this.isEdited.ga4PropertyId = true;
      this.params.ga4PropertyId = value;
      this.resetGA4DataStreams();
      this.getGA4DataStreams();
      this.updateParams();
    },

    changeDataStream(value) {
      this.isEdited.ga4DataStreamId = true;
      this.params.ga4DataStreamId = value;
      this.updateParams();
    },

    getGA4Accounts() {
      this.googleGA4API
        .getGA4Accounts()
        .then(response => {
          if (response.data && response.data.accounts) {
            this.data.ga4Accounts = response.data.accounts;
          } else {
            this.data.ga4Accounts = [];
          }
          this.$store.dispatch('set/setData', this.data);
          this.isLoading.ga4Accounts = false;
        })
        .catch(error => {
          console.error('ga-0004', error);
          this.goErrorPage();
        });
    },

    getGA4Properties() {
      if (!this.params.ga4AccountId) {
        return;
      }
      const params = {
        filter: `parent:${this.params.ga4AccountId}`
      };
      this.googleGA4API
        .getGA4Properties(params)
        .then(response => {
          if (response.data && response.data.properties) {
            this.data.ga4Properties = response.data.properties;
          } else {
            this.data.ga4Properties = [];
          }
          this.$store.dispatch('set/setData', this.data);
          this.isLoading.ga4Properties = false;
        })
        .catch(error => {
          console.error('ga-0005', error);
          this.goErrorPage();
        });
    },

    getGA4DataStreams() {
      if (!this.params.ga4PropertyId) {
        return;
      }
      this.googleGA4API
        .getGA4DataStreams(this.params.ga4PropertyId)
        .then(response => {
          if (response.data && response.data.dataStreams) {
            this.data.ga4DataStreams = response.data.dataStreams;
          } else {
            this.data.ga4DataStreams = [];
          }
          this.$store.dispatch('set/setData', this.data);
          this.isLoading.ga4DataStreams = false;
        })
        .catch(error => {
          console.error('ga-0006', error);
          this.goErrorPage();
        });
    },

    getSelectOptions(array) {
      return array.map(item => {
        return {
          label: item.displayName,
          value: item.name
        };
      });
    },

    goErrorPage() {
      this.$router.push({ name: 'setErrorGA' });
    },

    resetGA4Properties() {
      this.data.ga4Properties = [];
      this.isEdited.ga4PropertyId = false;
      this.isLoading.ga4Properties = true;
      this.params.ga4PropertyId = '';
      this.$store.dispatch('set/setData', this.data);
    },

    resetGA4DataStreams() {
      this.data.ga4DataStreams = [];
      this.isEdited.ga4DataStreamId = false;
      this.isLoading.ga4DataStreams = true;
      this.params.ga4DataStreamId = '';
      this.$store.dispatch('set/setData', this.data);
    },

    submit() {
      if (this.isError) {
        return;
      }
      this.$router.push({ name: 'setGSC' });
    },

    updateParams() {
      this.$store.dispatch('set/setParams', this.params);
      this.$store.dispatch('set/setEdited', this.isEdited);
    }
  }
};
</script>
